
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * LegalDocumentGetParams struct
 */
class LegalDocumentGetParams {
  /**
   * Initialize struct
   * @param {!number=} documentId
   * @param {!boolean=} includeDisabled
   * @constructor
   */
  constructor (documentId = new UInt64(0, 0), includeDisabled = false) {
    this.DocumentId = documentId
    this.IncludeDisabled = includeDisabled
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LegalDocumentGetParams}
   * @param {!LegalDocumentGetParams} other Other struct
   * @returns {!LegalDocumentGetParams} This struct
   */
  copy (other) {
    if (other.DocumentId != null) {
      this.DocumentId = UInt64.fromNumber(other.DocumentId)
    } else {
      this.DocumentId = null
    }
    if (other.IncludeDisabled != null) {
      this.IncludeDisabled = other.IncludeDisabled
    } else {
      this.IncludeDisabled = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LegalDocumentGetParams}
   * @returns {!LegalDocumentGetParams} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LegalDocumentGetParamsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LegalDocumentGetParamsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LegalDocumentGetParams}
   * @param {!LegalDocumentGetParams} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LegalDocumentGetParams)) {
      throw new TypeError('Instance of LegalDocumentGetParams is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LegalDocumentGetParams}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DocumentId: ((this.DocumentId != null) ? this.DocumentId.toNumber() : null),
      IncludeDisabled: ((this.IncludeDisabled != null) ? this.IncludeDisabled : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LegalDocumentGetParams.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LegalDocumentGetParams} other Object value
   * @returns {!LegalDocumentGetParams} Created struct
   */
  static fromObject (other) {
    return new LegalDocumentGetParams().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LegalDocumentGetParams}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LegalDocumentGetParams.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LegalDocumentGetParams}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 15025
  }
}

export { LegalDocumentGetParams };

/**
 * Fast Binary Encoding LegalDocumentGetParams field model
 */
class FieldModelLegalDocumentGetParams extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DocumentId = new fbe.FieldModelUInt64(buffer, 4 + 4)
    this.IncludeDisabled = new fbe.FieldModelBool(buffer, this.DocumentId.fbeOffset + this.DocumentId.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DocumentId.fbeSize + this.IncludeDisabled.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DocumentId.fbeExtra + this.IncludeDisabled.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLegalDocumentGetParams.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 15025
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DocumentId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentId.fbeSize

    if ((fbeCurrentSize + this.IncludeDisabled.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IncludeDisabled.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IncludeDisabled.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!LegalDocumentGetParams} fbeValue Default value, defaults is new LegalDocumentGetParams()
   * @returns {!LegalDocumentGetParams} LegalDocumentGetParams value
   */
  get (fbeValue = new LegalDocumentGetParams()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!LegalDocumentGetParams} fbeValue LegalDocumentGetParams value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentId.fbeSize) <= fbeStructSize) {
      fbeValue.DocumentId = this.DocumentId.get()
    } else {
      fbeValue.DocumentId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentId.fbeSize

    if ((fbeCurrentSize + this.IncludeDisabled.fbeSize) <= fbeStructSize) {
      fbeValue.IncludeDisabled = this.IncludeDisabled.get()
    } else {
      fbeValue.IncludeDisabled = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IncludeDisabled.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLegalDocumentGetParams}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {!LegalDocumentGetParams} fbeValue LegalDocumentGetParams value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLegalDocumentGetParams}
   * @param {LegalDocumentGetParams} fbeValue LegalDocumentGetParams value
   */
  setFields (fbeValue) {
    this.DocumentId.set(fbeValue.DocumentId)
    this.IncludeDisabled.set(fbeValue.IncludeDisabled)
  }
}

export { FieldModelLegalDocumentGetParams };

/**
 * Fast Binary Encoding LegalDocumentGetParams model
 */
class LegalDocumentGetParamsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLegalDocumentGetParams(this.buffer, 4)
  }

  /**
   * Get the LegalDocumentGetParams model
   * @this {!LegalDocumentGetParamsModel}
   * @returns {!FieldModelLegalDocumentGetParams} model LegalDocumentGetParams model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LegalDocumentGetParamsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LegalDocumentGetParamsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LegalDocumentGetParamsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LegalDocumentGetParamsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLegalDocumentGetParams.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LegalDocumentGetParamsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LegalDocumentGetParamsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LegalDocumentGetParamsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LegalDocumentGetParamsModel}
   * @param {!LegalDocumentGetParams} value LegalDocumentGetParams value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LegalDocumentGetParamsModel}
   * @param {!LegalDocumentGetParams} value LegalDocumentGetParams value, defaults is new LegalDocumentGetParams()
   * @return {!object} Deserialized LegalDocumentGetParams value and its size
   */
  deserialize (value = new LegalDocumentGetParams()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LegalDocumentGetParams(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LegalDocumentGetParams(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LegalDocumentGetParamsModel}
   * @param {!number} prev Previous LegalDocumentGetParams model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LegalDocumentGetParamsModel };
